/* eslint-disable no-magic-numbers, max-params, no-bitwise, max-lines */


// RGB stands for SRGB


// adapted from
// https://github.com/bottosson/bottosson.github.io/blob/3d3f17644d7f346e1ce1ca08eb8b01782eea97af/misc/colorpicker/colorconversion.js

// https://bottosson.github.io/posts/oklab/
// https://bottosson.github.io/posts/colorpicker/

function convertRGBToOkLab ({red, green, blue}) {
    red   = convertToLinear(red)
    green = convertToLinear(green)
    blue  = convertToLinear(blue)
    const l = Math.cbrt(0.4122214708 * red + 0.5363325363 * green + 0.0514459929 * blue)
    const m = Math.cbrt(0.2119034982 * red + 0.6806995451 * green + 0.1073969566 * blue)
    const s = Math.cbrt(0.0883024619 * red + 0.2817188376 * green + 0.6299787005 * blue)

    const L = 0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s
    const a = 1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s
    const b = 0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s

    return {L, a, b}
}


// warning : the H here is between 0 and 1 (not 0 and 360 like oklch)
function convertRGBToOkLabWithCH (params) {
    let {L, a, b} = convertRGBToOkLab(params)
    const C = Math.sqrt(a * a + b * b)
    let h = Math.atan2(b, a) / (2 * Math.PI)
    if (h < 0) {
        h += 1
    }
    return {L, a, b, C, h}
}




// can do OkLab or OkLCH
// L between 0 and 1
// then either :  a and b between -0.5 and 0.5
// or h between 0 and 360, and C between 0 and 0.5
// beware : there might be clamping, as sRGB is not able to reproduce the color (often for C > 0.3)
// if resulting red, green, or blue are exactly 0 or 1, this indicates clamping
function convertOkToRGB (params) {
    const {red, green, blue} = convertOkToLinearRGB(params)

    const {opacity = 1} = params
    return {
        red:   convertToSRGB(red),
        green: convertToSRGB(green),
        blue:  convertToSRGB(blue),
        opacity
    }
}



const k1Factor = 0.2 * 1.25 * 1.25
function convertOkHSLToRGB ({hue, saturation = 0.9, lightness = 0.7, opacity = 1}) {

    if (lightness > 0.997) {
        return {
            red:   1,
            green: 1,
            blue:  1
        }
    } else if (lightness < 0.003) {
        return {
            red:   0,
            green: 0,
            blue:  0
        }
    }

    const angle = 2 * Math.PI * hue
    const a_ = Math.cos(angle)
    const b_ = Math.sin(angle)
    const L  = toeInv(lightness)

    const {C0, Cmid, Cmax} = getCs(L, a_, b_)

    let t
    let k0
    let k1
    let k2
    if (saturation < 0.8) {
        t = 1.25 * saturation
        k0 = 0
        k1 = 0.8 * C0
        k2 = (1 - k1 / Cmid)
    } else {
        t = 5 * (saturation - 0.8)
        k0 = Cmid
        k1 = k1Factor * Cmid * Cmid / C0
        k2 = 1 - (k1 / (Cmax - Cmid))
    }

    let C = k0 + (t * k1 / (1 - k2 * t))

    return convertOkToRGB({L, a: C * a_, b: C * b_, opacity})

}



function convertRGBToOkHSL (params) {
    let {L, a, b, C, h} = convertRGBToOkLabWithCH(params)

    let a_ = a / C
    let b_ = b / C

    const {C0, Cmid, Cmax} = getCs(L, a_, b_)

    let s
    if (C < Cmid) {
        let k1 = 0.8 * C0
        let t = C / (k1 + (1 - k1 / Cmid) * C)
        s = t * 0.8
    } else {
        let k1 = Cmid * Cmid * k1Factor / C0
        let k2 = (1 - k1) / (Cmax - Cmid)

        let t = (C - Cmid) / (k1 + k2 * (C - Cmid))
        s = 0.8 + 0.2 * t
    }

    let lightness = toe(L)
    return {hue: h, saturation: s, lightness}
}


function convertRGBToOkHSV (params) {
    let {L, a, b, C, h: hue} = convertRGBToOkLabWithCH(params)

    if (L === 0) {
        return {
            hue: 0,
            saturation: 0,
            value: 0
        }
    }

    let a_ = a / C
    let b_ = b / C

    let [Smax, T] = getSTmax(a_, b_)
    let S0 = 0.5
    let k = 1 - S0 / Smax

    let t = T / (C + L * T)
    let Lv = t * L
    let Cv = t * C

    let Lvt = toeInv(Lv)
    let Cvt = Cv * Lvt / Lv

    let {red, green, blue} = convertOkToLinearRGB({L: Lvt, a: a_ * Cvt, b: b_ * Cvt})
    let scaleL = Math.cbrt(1 / Math.max(red, green, blue, 0))

    L /= scaleL
    C /= scaleL

    C = C * toe(L) / L
    L = toe(L)

    let value      = L / Lv
    let saturation = (S0 + T) * Cv / ((T * S0) + T * k * Cv)

    return {hue, saturation, value}
}






function convertOkHSVToRGB ({hue, saturation, value, opacity = 1}) {

    if (value < 0.003) {
        return {
            red:   0,
            green: 0,
            blue:  0
        }
    }

    const angle = 2 * Math.PI * hue
    const a_ = Math.cos(angle)
    const b_ = Math.sin(angle)

    let STmax = getSTmax(a_, b_)
    let Smax = STmax[0]
    let S0 = 0.5
    let T  = STmax[1]
    let k  = 1 - S0 / Smax

    let Lv = 1 - saturation * S0 / (S0 + T - T * k * saturation)
    let Cv = saturation * T * S0 / (S0 + T - T * k * saturation)


    let L = value * Lv
    let C = value * Cv

    let Lvt = toeInv(Lv)
    let Cvt = Cv * Lvt / Lv

    let Lnew = toeInv(L)
    C = C * Lnew / L
    L = Lnew

    let {red, green, blue} = convertOkToLinearRGB({L: Lvt, a: a_ * Cvt, b:  b_ * Cvt})
    let scaleL = Math.cbrt(1 / (Math.max(red, green, blue, 0)))

    // remove to see effect without rescaling
    L *= scaleL
    C *= scaleL

    return convertOkToRGB({L, a: C * a_, b: C * b_, opacity})
}




function convertOkToLinearRGB ({L, a = false, b, C, h}) {

    if (a === false) {
        let hAngle = h * 2 * Math.PI / 360
        a = C * Math.cos(hAngle)
        b = C * Math.sin(hAngle)
    }

    let l = L + 0.3963377774 * a + 0.2158037573 * b
    let m = L - 0.1055613458 * a - 0.0638541728 * b
    let s = L - 0.0894841775 * a - 1.2914855480 * b

    l *= l * l
    m *= m * m
    s *= s * s

    return applyOkCoefs(l, m, s)
}




function applyOkCoefs (l, m, s) {
    const red   = 4.0767416621  * l - 3.3077115913 * m + 0.2309699292 * s
    const green = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s
    const blue  = -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s
    return {red, green, blue}
}


function getCs (L, a_, b_) {
    const cusp = findCusp(a_, b_)

    let Cmax = findGamutIntersection(a_, b_, L, 1, L, cusp)
    let STmax = getSTmax(a_, b_, cusp)

    const Smid = 0.11516993 + 1 / (
        7.44778970 + 4.15901240 * b_ +
        a_ * (-2.19557347 + 1.75198401 * b_ +
        a_ * (-2.13704948 - 10.02301043 * b_ +
        a_ * (-4.24894561 + 5.38770819 * b_ + 4.69891013 * a_)
        ))
    )

    const Tmid = 0.11239642 + 1 / (
        1.61320320 - 0.68124379 * b_ +
        a_ * (0.40370612  + 0.90148123 * b_ +
        a_ * (-0.27087943 + 0.61223990 * b_ +
        a_ * (0.00299215  - 0.45399568 * b_ - 0.14661872 * a_)
        ))
    )

    let k = Cmax / Math.min((L * STmax[0]), (1 - L) * STmax[1])


    let Ca = L * Smid
    let Cb = (1 - L) * Tmid
    const f1 = 1 / (1 / (Ca * Ca * Ca * Ca) + 1 / (Cb * Cb * Cb * Cb))
    let Cmid = 0.9 * k * Math.sqrt(Math.sqrt(f1))

    Ca = L * 0.4
    Cb = (1 - L) * 0.8
    const f2 = (1 / (Ca * Ca) + 1 / (Cb * Cb))
    const C0 = Math.sqrt(1 / f2)


    return {C0, Cmid, Cmax}
}




function findCusp (a, b) {
    let SCusp = computeOkLabMaxSaturation(a, b)

    // Convert to linear sRGB to find the first point where at least one of r,g or b >= 1:
    const {red, green, blue} = convertOkToLinearRGB({L: 1, a: SCusp * a, b: SCusp * b})

    let LCusp = Math.cbrt(1 / Math.max(red, green, blue))
    let CCusp = LCusp * SCusp

    return [LCusp, CCusp]
}



// Finds intersection of the line defined by
// L = L0 * (1 - t) + t * L1;
// C = t * C1;
// a and b must be normalized so a^2 + b^2 == 1
function findGamutIntersection (a, b, L1, C1, L0, cusp = findCusp(a, b)) {

    const [LCusp, CCusp] = cusp


    // Find the intersection for upper and lower half seprately
    let t
    if (((L1 - L0) * CCusp - (LCusp - L0) * C1) <= 0) {
        // Lower half

        t = CCusp * L0 / (C1 * LCusp + CCusp * (L0 - L1))
    } else {
        // Upper half

        // First intersect with triangle
        t = CCusp * (L0 - 1) / (C1 * (LCusp - 1) + CCusp * (L0 - L1))

        // Then one step Halley's method
        {
            let dL = L1 - L0
            let dC = C1

            let k_l = +0.3963377774 * a + 0.2158037573 * b
            let k_m = -0.1055613458 * a - 0.0638541728 * b
            let k_s = -0.0894841775 * a - 1.2914855480 * b

            let l_dt = dL + dC * k_l
            let m_dt = dL + dC * k_m
            let s_dt = dL + dC * k_s


            // If higher accuracy is required, 2 or 3 iterations of the following block can be used:
            {
                let L = L0 * (1 - t) + t * L1
                let C = t * C1

                let l_ = L + C * k_l
                let m_ = L + C * k_m
                let s_ = L + C * k_s

                let l = l_ * l_ * l_
                let m = m_ * m_ * m_
                let s = s_ * s_ * s_

                let ldt = 3 * l_dt * l_ * l_
                let mdt = 3 * m_dt * m_ * m_
                let sdt = 3 * s_dt * s_ * s_

                let ldt2 = 6 * l_dt * l_dt * l_
                let mdt2 = 6 * m_dt * m_dt * m_
                let sdt2 = 6 * s_dt * s_dt * s_

                let {red: r, green: g, blue: b0} = applyOkCoefs(l, m, s)
                r  -= 1
                g  -= 1
                b0 -= 1

                let {red: r1, green: g1, blue: b1} = applyOkCoefs(ldt, mdt, sdt)
                let {red: r2, green: g2, blue: b2} = applyOkCoefs(ldt2, mdt2, sdt2)


                let ur = 1 / (r1 - 0.5 * r * r2 / r1)
                let tr = (ur < 0 ? 10e5 : -r * ur)

                let ug = 1 / (g1 - 0.5 * g * g2 / g1)
                let tg = (ug < 0 ? 10e5 : -g * ug)

                let ub = 1 / (b1 - 0.5 * b0 * b2 / b1)
                let tb = (ub < 0 ? 10e5 : -b0 * ub)


                t += Math.min(tr, tg, tb)
            }
        }
    }

    return t
}



// Finds the maximum saturation possible for a given hue that fits in sRGB
// Saturation here is defined as S = C/L
// a and b must be normalized so a^2 + b^2 == 1
function computeOkLabMaxSaturation (a, b) {
    // Max saturation will be when one of r, g or b goes below zero.

    // Select different coefficients depending on which component goes below zero first
    let k0
    let k1
    let k2
    let k3
    let k4
    let wl
    let wm
    let ws

    if (-1.88170328 * a - 0.80936493 * b > 1) {
        // Red component
        k0 = +1.19086277
        k1 = +1.76576728
        k2 = +0.59662641
        k3 = +0.75515197
        k4 = +0.56771245
        wl = +4.0767416621
        wm = -3.3077115913
        ws = +0.2309699292
    } else if (1.81444104 * a - 1.19445276 * b > 1) {
        // Green component
        k0 = +0.73956515
        k1 = -0.45954404
        k2 = +0.08285427
        k3 = +0.12541070
        k4 = +0.14503204
        wl = -1.2684380046
        wm = +2.6097574011
        ws = -0.3413193965
    } else {
        // Blue component
        k0 = +1.35733652
        k1 = -0.00915799
        k2 = -1.15130210
        k3 = -0.50559606
        k4 = +0.00692167
        wl = -0.0041960863
        wm = -0.7034186147
        ws = +1.7076147010
    }

    // Approximate max saturation using a polynomial:
    let S = k0 + k1 * a + k2 * b + k3 * a * a + k4 * a * b

    // Do one step Halley's method to get closer
    // this gives an error less than 10e6, except for some blue hues where the dS/dh is close to infinite
    // this should be sufficient for most applications, otherwise do two/three steps

    let k_l = +0.3963377774 * a + 0.2158037573 * b
    let k_m = -0.1055613458 * a - 0.0638541728 * b
    let k_s = -0.0894841775 * a - 1.2914855480 * b

    {
        let l_ = 1 + S * k_l
        let m_ = 1 + S * k_m
        let s_ = 1 + S * k_s

        let l = l_ * l_ * l_
        let m = m_ * m_ * m_
        let s = s_ * s_ * s_

        let l_dS = 3 * k_l * l_ * l_
        let m_dS = 3 * k_m * m_ * m_
        let s_dS = 3 * k_s * s_ * s_

        let l_dS2 = 6 * k_l * k_l * l_
        let m_dS2 = 6 * k_m * k_m * m_
        let s_dS2 = 6 * k_s * k_s * s_

        let f  = wl * l     + wm * m     + ws * s
        let f1 = wl * l_dS  + wm * m_dS  + ws * s_dS
        let f2 = wl * l_dS2 + wm * m_dS2 + ws * s_dS2

        S -= f * f1 / (f1 * f1 - 0.5 * f * f2)
    }

    return S
}




const k1 = 0.206
const k2 = 0.03
const k3 = 1.1708737864

function toe (x) {
    let t = (k3 * x - k1)
    return 0.5 * (t + Math.sqrt(t * t + 4 * k2 * k3 * x))
}

function toeInv (x) {
    return x * (x + k1) / (k3 * (x + k2))
}






function getSTmax (a_, b_, cusp = findCusp(a_, b_)) {
    const [LCusp, CCusp] = cusp
    return [CCusp / LCusp, CCusp / (1 - LCusp)]
}


function convertToLinear (valueWithGamma) {
    if (valueWithGamma <= 0.04045) {
        return valueWithGamma / 12.92
    } else {
        return Math.pow((valueWithGamma + 0.055) / 1.055, 2.4)
    }
}


const gammaPower = 1 / 2.4
function convertToSRGB (linearValue) {
    if (linearValue < 0) {
        return 0
    } else if (linearValue <= 0.0031308) {
        return linearValue * 12.92
    } else {
        return Math.min(1, 1.055 * Math.pow(linearValue, gammaPower) - 0.055)
    }
}


// https://github.com/svgeesus/svgeesus.github.io/blob/master/Color/OKLab-notes.md
function distance (color1, color2) {
    const deltaL = color1.L - color2.L
    const deltaA = color1.a - color2.a
    const deltaB = color1.b - color2.b
    return Math.sqrt(deltaL * deltaL + deltaA * deltaA + deltaB * deltaB)
}



// https://www.w3.org/TR/css-color-4/#color-difference-2000

function deltaE2000 (reference, sample) { //eslint-disable-line complexity

    // Given a reference and a sample color,
    // both in CIE Lab,
    // calculate deltaE 2000.

    // This implementation assumes the parametric
    // weighting factors kL, kC and kH
    // (for the influence of viewing conditions)
    // are all 1, as seems typical.

    let {L: L1, a: a1, b: b1} = reference
    let {L: L2, a: a2, b: b2} = sample
    let C1 = Math.sqrt(a1 ** 2 + b1 ** 2)
    let C2 = Math.sqrt(a2 ** 2 + b2 ** 2)

    let Cbar = (C1 + C2) / 2 // mean Chroma

    // calculate a-axis asymmetry factor from mean Chroma
    // this turns JND ellipses for near-neutral colors back into circles
    let C7 = Math.pow(Cbar, 7)
    const Gfactor = Math.pow(25, 7)
    let G = 0.5 * (1 - Math.sqrt(C7 / (C7 + Gfactor)))

    // scale a axes by asymmetry factor
    // this by the way is why there is no Lab2000 color space
    let adash1 = (1 + G) * a1
    let adash2 = (1 + G) * a2

    // calculate new Chroma from scaled a and original b axes
    let Cdash1 = Math.sqrt(adash1 ** 2 + b1 ** 2)
    let Cdash2 = Math.sqrt(adash2 ** 2 + b2 ** 2)

    // calculate new hues, with zero hue for true neutrals
    // and in degrees, not radians
    const π = Math.PI
    const r2d = 180 / π
    const d2r = π / 180
    let h1 = (adash1 === 0 && b1 === 0) ? 0 : Math.atan2(b1, adash1)
    let h2 = (adash2 === 0 && b2 === 0) ? 0 : Math.atan2(b2, adash2)

    if (h1 < 0) {
        h1 += 2 * π
    }
    if (h2 < 0) {
        h2 += 2 * π
    }

    h1 *= r2d
    h2 *= r2d

    // Lightness and Chroma differences; sign matters
    let ΔL = L2 - L1
    let ΔC = Cdash2 - Cdash1

    // Hue difference, taking care to get the sign correct
    let hdiff = h2 - h1
    let hsum = h1 + h2
    let habs = Math.abs(hdiff)
    let Δh

    if (Cdash1 * Cdash2 === 0) {
        Δh = 0
    } else if (habs <= 180) {
        Δh = hdiff
    } else if (hdiff > 180) {
        Δh = hdiff - 360
    } else if (hdiff < -180) {
        Δh = hdiff + 360
    } else {
        console.log("the unthinkable has happened")
    }

    // weighted Hue difference, more for larger Chroma
    let ΔH = 2 * Math.sqrt(Cdash2 * Cdash1) * Math.sin(Δh * d2r / 2)

    // calculate mean Lightness and Chroma
    let Ldash = (L1 + L2) / 2
    let Cdash = (Cdash1 + Cdash2) / 2
    let Cdash7 = Math.pow(Cdash, 7)

    // Compensate for non-linearity in the blue region of Lab.
    // Four possibilities for hue weighting factor,
    // depending on the angles, to get the correct sign
    let hdash
    if (Cdash1 === 0 && Cdash2 === 0) {
        hdash = hsum   // which should be zero
    } else if (habs <= 180) {
        hdash = hsum / 2
    } else if (hsum < 360) {
        hdash = (hsum + 360) / 2
    } else {
        hdash = (hsum - 360) / 2
    }

    // positional corrections to the lack of uniformity of CIELAB
    // These are all trying to make JND ellipsoids more like spheres

    // SL Lightness crispening factor
    // a background with L=50 is assumed
    let lsq = (Ldash - 50) ** 2
    let SL = 1 + ((0.015 * lsq) / Math.sqrt(20 + lsq))

    // SC Chroma factor, similar to those in CMC and deltaE 94 formulae
    let SC = 1 + 0.045 * Cdash

    // Cross term T for blue non-linearity
    let T = 1
    T -= (0.17 * Math.cos((hdash - 30) * d2r))
    T += (0.24 * Math.cos(2 * hdash    * d2r))
    T += (0.32 * Math.cos(((3 * hdash) + 6)  * d2r))
    T -= (0.20 * Math.cos(((4 * hdash) - 63) * d2r))

    // SH Hue factor depends on Chroma,
    // as well as adjusted hue angle like deltaE94.
    let SH = 1 + 0.015 * Cdash * T

    // RT Hue rotation term compensates for rotation of JND ellipses
    // and Munsell constant hue lines
    // in the medium-high Chroma blue region
    // (Hue 225 to 315)
    let Δθ = 30 * Math.exp(-1 * (((hdash - 275) / 25) ** 2))
    let RC = 2 * Math.sqrt(Cdash7 / (Cdash7 + Gfactor))
    let RT = -1 * Math.sin(2 * Δθ * d2r) * RC

    // Finally calculate the deltaE, term by term as root sum of squares
    let dE = (ΔL / SL) ** 2
    dE += (ΔC / SC) ** 2
    dE += (ΔH / SH) ** 2
    dE += RT * (ΔC / SC) * (ΔH / SH)
    return Math.sqrt(dE)

}


export default {
    convertRGBToOkLab,
    convertRGBToOkHSL,
    convertRGBToOkHSV,
    convertOkToRGB,
    convertOkLCHToRGB: convertOkToRGB,
    convertOkLabToRGB: convertOkToRGB,
    convertOkHSVToRGB,
    convertOkHSLToRGB,
    distance,
    deltaE2000
}

